import React, { useMemo, useRef, useState } from 'react'
import { StyledIcon, StyledIconButton } from './../styles'
import { alpha, Box, styled, SwipeableDrawer, Typography, useTheme } from '@mui/material'
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons'
import { faSquareRootVariable } from '@fortawesome/pro-solid-svg-icons/faSquareRootVariable'
import { useSnapshot } from 'valtio'
import Calculator from './Calculator'
import Highlight from './Highlight'
import ToolsMenuItem from './ToolsMenuItem'
import { AccessibilityTestMenuContainer } from '@app/components/ui'
import Strikethrough from './Strikethrough'
import { CurrentUserAssessmentState } from '@app/storage'
import { FormulaSheetDialog, isFormulaSheetProgram, useIsFormulaSheetEnabled } from '@app/components/common/FormulaSheet'

type Props = {
  isReviewMode?: boolean
}

const MobileToolsMenu: React.FC<Props> = ({ isReviewMode = false }) => {
  const [open, setOpen] = useState(false)
  const [isFormulaSheetOpen, setIsFormulaSheetOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const { program } = useSnapshot(CurrentUserAssessmentState)
  const isFormulaSheetEnabled = useIsFormulaSheetEnabled()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  // The drawer (zIndex 99999) sits above the modal, so close it before showing the sheet.
  const toggleFormulaSheet = () => {
    if (!isFormulaSheetOpen) setOpen(false)
    setIsFormulaSheetOpen(!isFormulaSheetOpen)
  }

  const theme = useTheme()

  const headerHeight = useMemo(() => theme.spacing(14.5), [theme])

  return (
    <StyledContainer>
      <StyledIconButton
        ref={menuButtonRef}
        aria-label='Test Tools Menu'
        data-automation-id='test-tools-menu-button'
        onClick={() => setOpen(prev => !prev)}
      >
        <StyledIcon icon={faEllipsis} aria-label='More options' />
      </StyledIconButton>

      <SwipeableDrawer
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        anchor='bottom'
        disableSwipeToOpen={false}
        slotProps={{
          root: {
            style: {
              top: headerHeight,
              zIndex: 99999,
            },
          },
          backdrop: {
            sx: {
              top: theme.spacing(14.5),
              height: `calc(100% - ${headerHeight})`,
              backgroundColor: alpha('#B0BEC5', 0.5),
            },
          },
        }}
        PaperProps={{
          sx: {
            maxHeight: '60vh',
          },
        }}
      >
        <SheetContent>
          <Calculator isReviewMode={isReviewMode} isMobile />
          {isFormulaSheetEnabled && isFormulaSheetProgram(program) && (
            <ToolsMenuItem
              icon={faSquareRootVariable}
              label='Formula Sheet'
              checked={isFormulaSheetOpen}
              onChange={toggleFormulaSheet}
            />
          )}
          <Strikethrough isReviewMode={isReviewMode} isMobile />
          <Highlight isReviewMode={isReviewMode} isMobile />
          <AccessibilityContainer>
            <AccessibilityLabel variant='h5'>Accessibility Tools</AccessibilityLabel>
            <AccessibilityTestMenuContainer isReviewMode={isReviewMode} isMobile />
          </AccessibilityContainer>
        </SheetContent>
      </SwipeableDrawer>

      <FormulaSheetDialog
        open={isFormulaSheetOpen}
        onClose={() => setIsFormulaSheetOpen(false)}
        // a11y: the switch that opened the sheet is hidden with the closed drawer, so return focus to the menu button.
        // When the switch closed it, the drawer is open and focus stays on the switch.
        onExited={() => {
          if (!open) menuButtonRef.current?.focus()
        }}
      />
    </StyledContainer>
  )
}

export default MobileToolsMenu

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}))

const SheetContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: theme.spacing(2, 0),
}))

const AccessibilityContainer = styled(SheetContent)(({ theme }) => ({
  borderTop: `1px solid var(--bluegrey-100)`,
  padding: 0,
  marginTop: theme.spacing(3),
}))

const AccessibilityLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.additionalColors['bg_500'],
  padding: theme.spacing(3, 4),
}))
